import { adaptSesionAuth, adaptSesiones } from '@/shared/domain/adapters';
import type { PerfilTipo, SesionActiva, SesionAuth } from '@/shared/domain/types';
import { apiAxios } from '../api';
import { toResult } from '../handlers';
import type { ApiResult } from '../types';
import { priv, pub, segmento, sinCuerpo, type RequestOpts } from './common';

/** Autenticación (/v1/auth). Las rutas públicas NO llevan Bearer; `refresh` tampoco, para no entrar en bucle. */
export interface RegistroInput {
  email: string;
  password: string;
  nombre?: string;
  perfilTipo?: PerfilTipo;
}

export interface CambiarPasswordInput {
  passwordActual: string;
  passwordNueva: string;
}

/** Cuerpo con `refresh_token` solo si hay uno (en modo cookie viaja la cookie y el cuerpo va vacío). */
const cuerpoRefresh = (refreshToken: string | null | undefined): Record<string, string> =>
  refreshToken ? { refresh_token: refreshToken } : {};

export const authApi = {
  registro: (input: RegistroInput, opts?: RequestOpts): Promise<ApiResult<SesionAuth>> =>
    toResult(
      apiAxios.post({
        url: '/auth/registro',
        data: {
          email: input.email,
          password: input.password,
          ...(input.nombre ? { nombre: input.nombre } : {}),
          ...(input.perfilTipo ? { perfil_tipo: input.perfilTipo } : {}),
        },
        config: pub(opts),
      }),
      adaptSesionAuth,
    ),

  login: (email: string, password: string, opts?: RequestOpts): Promise<ApiResult<SesionAuth>> =>
    toResult(
      apiAxios.post({ url: '/auth/login', data: { email, password }, config: pub(opts) }),
      adaptSesionAuth,
    ),

  /** Renueva la sesión; rota el refresh token. En modo cookie se llama sin token. */
  refresh: (refreshToken: string | null, opts?: RequestOpts): Promise<ApiResult<SesionAuth>> =>
    toResult(
      apiAxios.post({
        url: '/auth/refresh',
        data: cuerpoRefresh(refreshToken),
        config: pub(opts),
      }),
      adaptSesionAuth,
    ),

  /** Idempotente: responde 204 aunque el token ya no exista. */
  logout: (refreshToken: string | null, opts?: RequestOpts): Promise<ApiResult<void>> =>
    toResult(
      apiAxios.post({ url: '/auth/logout', data: cuerpoRefresh(refreshToken), config: pub(opts) }),
      sinCuerpo,
    ),

  logoutTodos: (opts?: RequestOpts): Promise<ApiResult<void>> =>
    toResult(apiAxios.post({ url: '/auth/logout-todos', config: priv(opts) }), sinCuerpo),

  /** Responde 202 siempre (no revela si el correo existe). */
  recuperar: (email: string, opts?: RequestOpts): Promise<ApiResult<void>> =>
    toResult(
      apiAxios.post({ url: '/auth/recuperar', data: { email }, config: pub(opts) }),
      sinCuerpo,
    ),

  restablecer: (token: string, password: string, opts?: RequestOpts): Promise<ApiResult<void>> =>
    toResult(
      apiAxios.post({ url: '/auth/restablecer', data: { token, password }, config: pub(opts) }),
      sinCuerpo,
    ),

  verificarEmail: (token: string, opts?: RequestOpts): Promise<ApiResult<void>> =>
    toResult(
      apiAxios.post({ url: '/auth/verificar-email', data: { token }, config: pub(opts) }),
      sinCuerpo,
    ),

  reenviarVerificacion: (opts?: RequestOpts): Promise<ApiResult<void>> =>
    toResult(apiAxios.post({ url: '/auth/reenviar-verificacion', config: priv(opts) }), sinCuerpo),

  cambiarPassword: (input: CambiarPasswordInput, opts?: RequestOpts): Promise<ApiResult<void>> =>
    toResult(
      apiAxios.post({
        url: '/auth/cambiar-password',
        data: { password_actual: input.passwordActual, password_nueva: input.passwordNueva },
        config: priv(opts),
      }),
      sinCuerpo,
    ),

  listarSesiones: (opts?: RequestOpts): Promise<ApiResult<SesionActiva[]>> =>
    toResult(apiAxios.get({ url: '/auth/sesiones', config: priv(opts) }), adaptSesiones),

  cerrarSesion: (id: string, opts?: RequestOpts): Promise<ApiResult<void>> =>
    toResult(
      apiAxios.delete({ url: `/auth/sesiones/${segmento(id)}`, config: priv(opts) }),
      sinCuerpo,
    ),
};
